import { DvfModelGridMetrics } from '~/domain/dvf/dvfField';
import { DcmJsDatasetWrapper } from './dcmJsWrapper';
import {
    DeformableGrid,
    DicomDeformableRegistration,
} from './dicomDeformableRegistration';
import { DicomDataset } from './engine/dataset';
import { DicomFile } from './engine/reader';
import { Tag } from './engine/tag';
import * as tags from './engine/tags';

import {
    DVF_QA_GROUP,
    DVF_QA_PRIVATE_CREATOR,
} from './deformableRegistrationTags';
import {
    deformationMatrix,
    finiteTriple,
    finiteValues,
    modelGridMetrics,
    movingFrameOfReferenceUid,
    privateValuesFromBytes,
} from './deformableRegistrationValues';

export function readDeformableRegistration(
    file: DicomFile
): DicomDeformableRegistration | null {
    const dataset = file.dataset;
    const grid = readGrid(dataset);
    if (!grid) {
        return null;
    }
    const fixedFrameOfReferenceUid =
        dataset.string(tags.FRAME_OF_REFERENCE_UID) ?? '';
    return new DicomDeformableRegistration(
        dataset.string(tags.SOP_INSTANCE_UID) ?? '',
        fixedFrameOfReferenceUid,
        movingFrameOfReferenceUid(
            fixedFrameOfReferenceUid,
            readRelatedFrameOfReferenceUids(dataset)
        ),
        dataset.string(tags.CONTENT_LABEL) ?? '',
        grid,
        // Wraps the file, not its projection, so a later write reproduces the source bytes.
        new DcmJsDatasetWrapper(file)
    );
}

function readRelatedFrameOfReferenceUids(dataset: DicomDataset): string[] {
    const fixed = dataset.string(tags.FRAME_OF_REFERENCE_UID);
    const related = new Set<string>();
    for (const item of dataset.sequence(
        tags.DEFORMABLE_REGISTRATION_SEQUENCE
    )) {
        const uid = item.string(tags.SOURCE_FRAME_OF_REFERENCE_UID);
        if (uid && uid !== fixed) {
            related.add(uid);
        }
    }
    return [...related];
}

function readGrid(dataset: DicomDataset): DeformableGrid | null {
    for (const item of dataset.sequence(
        tags.DEFORMABLE_REGISTRATION_SEQUENCE
    )) {
        const grid = item.sequence(
            tags.DEFORMABLE_REGISTRATION_GRID_SEQUENCE
        )[0];
        if (!grid) {
            continue;
        }
        const size = finiteTriple(grid.numbers(tags.GRID_DIMENSIONS));
        const spacing = finiteTriple(grid.numbers(tags.GRID_RESOLUTION));
        const origin = finiteTriple(grid.numbers(tags.IMAGE_POSITION_PATIENT));
        const orientation = finiteValues(
            grid.numbers(tags.IMAGE_ORIENTATION_PATIENT),
            6
        );
        const data = float32(grid.bytes(tags.VECTOR_GRID_DATA));
        const preDeformationMatrix = matrixOf(
            item,
            tags.PRE_DEFORMATION_MATRIX_REGISTRATION_SEQUENCE
        );
        const postDeformationMatrix = matrixOf(
            item,
            tags.POST_DEFORMATION_MATRIX_REGISTRATION_SEQUENCE
        );
        // A malformed grid is skipped, never guessed.
        if (
            !size ||
            !spacing ||
            !origin ||
            !orientation ||
            !data ||
            !preDeformationMatrix ||
            !postDeformationMatrix ||
            data.length !== size[0] * size[1] * size[2] * 3
        ) {
            continue;
        }
        return {
            size,
            spacing,
            origin,
            orientation,
            data,
            preDeformationMatrix,
            postDeformationMatrix,
            metrics: readModelGridMetrics(item),
        };
    }
    return null;
}

function readModelGridMetrics(item: DicomDataset): DvfModelGridMetrics | null {
    const base = dvfQaPrivateBlockBase(item);
    if (base === null) {
        return null;
    }
    return modelGridMetrics((offset, count, bytesPerValue) => {
        const tag = ((DVF_QA_GROUP << 16) | (base + offset)) as Tag;
        const bytes = item.bytes(tag);
        return bytes
            ? privateValuesFromBytes(
                  new DataView(
                      bytes.buffer,
                      bytes.byteOffset,
                      bytes.byteLength
                  ),
                  count,
                  bytesPerValue
              )
            : finiteValues(item.numbers(tag), count);
    });
}

function dvfQaPrivateBlockBase(item: DicomDataset): number | null {
    for (const tag of item.tags()) {
        if (tag >>> 16 !== DVF_QA_GROUP) {
            continue;
        }
        const element = tag & 0xffff;
        if (element < 0x0010 || element > 0x00ff) {
            continue;
        }
        if (item.string(tag)?.trim() === DVF_QA_PRIVATE_CREATOR) {
            return element << 8;
        }
    }
    return null;
}

function matrixOf(item: DicomDataset, sequenceTag: Tag): number[] | null {
    const first = item.sequence(sequenceTag)[0];
    return deformationMatrix(
        !!first,
        first?.numbers(tags.FRAME_OF_REFERENCE_TRANSFORMATION_MATRIX)
    );
}

function float32(bytes: Uint8Array | undefined): Float32Array | null {
    if (!bytes || bytes.byteLength % 4 !== 0) {
        return null;
    }
    // DICOM pads elements to even length, not to 4, so a valid grid may start unaligned.
    if (bytes.byteOffset % 4 !== 0) {
        return new Float32Array(bytes.slice().buffer);
    }
    return new Float32Array(
        bytes.buffer,
        bytes.byteOffset,
        bytes.byteLength / 4
    );
}
